import { SyntheticEvent, useState } from 'react';
import Link from 'next/link';
import { Modal } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { black, slate } from '@/lib/colours';
import { Box, IconButton, Stack, styled, Typography } from '@/primitives';
import { footerMessages } from './messages';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { FOOTER_HEIGHT, MOBILE_FOOTER_HEIGHT } from '@/configs/layout';

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 4px 4px;
  margin-top: 0 !important;
  text-transform: capitalize;
`;

const EULA_LINK = '/abri-eula.pdf';
const PRIVACY_POLICY_LINK = '/privacy-policy.pdf';
const SUPPORT_LINK = '/support';

export default function ShellFooter() {
  const [showModal, setShowModal] = useState(false);
  const [modalLink, setModalLink] = useState('');

  function handleOpenEula(e: SyntheticEvent) {
    e.preventDefault();
    setModalLink(EULA_LINK);
    setShowModal(true);
  }

  function handleOpenPrivacyPolicy(e: SyntheticEvent) {
    e.preventDefault();
    setModalLink(PRIVACY_POLICY_LINK);
    setShowModal(true);
  }

  function handleCloseModal() {
    setModalLink('');
    setShowModal(false);
  }

  return (
    <Stack
      component="footer"
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: { xs: '20px', md: '32px' },
        border: `1px solid ${slate[200]}`,
        minHeight: { xs: MOBILE_FOOTER_HEIGHT, md: FOOTER_HEIGHT },
        background: 'white',
        zIndex: 1300,

        '@media print': {
          display: 'none',
        },
      }}
    >
      <Stack direction="row" gap={{ xs: 0, sm: '12px', md: '24px' }}>
        <StyledLink href={PUBLIC_ROUTES.HOME}>
          <Typography
            color={slate[500]}
            component="span"
            sx={{
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
            variant="labelmedium"
          >
            © {new Date().getFullYear()} ABRI
          </Typography>
        </StyledLink>
        <StyledLink href={EULA_LINK} onClick={handleOpenEula}>
          <Typography
            color={slate[500]}
            component="span"
            sx={{
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
            variant="labelmedium"
          >
            {footerMessages['footer.terms.label']}
          </Typography>
        </StyledLink>
        <StyledLink
          href={PRIVACY_POLICY_LINK}
          onClick={handleOpenPrivacyPolicy}
        >
          <Typography
            color={slate[500]}
            component="span"
            sx={{
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
            variant="labelmedium"
          >
            {footerMessages['footer.privacy.label']}
          </Typography>
        </StyledLink>
        <Modal open={showModal} onClose={handleCloseModal}>
          <>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                height: '85%',
                bgcolor: 'background.paper',
                boxShadow: 24,
              }}
            >
              <iframe
                src={modalLink}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
          </>
        </Modal>
      </Stack>
      <Stack alignItems="center" direction="row">
        <Typography
          color={black[900]}
          component="span"
          sx={{
            display: { xs: 'none', sm: 'unset' },
            mr: '28px',
          }}
          variant="labelmedium"
        >
          v{process.env.version}.
          {process.env.NEXT_PUBLIC_PRODUCTION_BUILD_NUMBER}.
          {process.env.NEXT_PUBLIC_STAGING_BUILD_NUMBER}.
          {process.env.NEXT_PUBLIC_DEV_BUILD_NUMBER}
        </Typography>
        <StyledLink href={SUPPORT_LINK} target="_blank">
          <Typography
            color={black[900]}
            component="span"
            sx={{
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
              whiteSpace: 'nowrap',
            }}
            variant="labelmedium"
          >
            {footerMessages['footer.support.label']}
          </Typography>
        </StyledLink>
        <IconButton
          sx={{ height: '18px', width: '18px' }}
          onClick={() => {
            document
              .getElementById('main')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
            document
              .getElementById('shell')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <ArrowDropUpIcon
            sx={{
              color: black[900],
              fontSize: '12px',
            }}
          />
        </IconButton>
      </Stack>
    </Stack>
  );
}
